import { Dialog, DialogContent, TextField, Box } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import isEqual from "lodash/isEqual";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { Props, State } from "./interfaces";
import { FieldsContainer } from "./styles";
import ConfirmationChangesDialog from "../../../../containers/ConfirmationChangesDialog/ConfirmationChangesDialog";
import DialogActions from "../../../../containers/DialogActions/DialogActions";
import DialogTitle from "../../../../containers/DialogTitle/DialogTitle";
import LoadingOverlay from "../../../../containers/LoadingOverlay/LoadingOverlay";
import { useAppSelector } from "../../../../custom-hooks/redux/redux";
import { CompanyLegalEntityRef } from "../../../../models/company-accounts/company-legal-entities/company-legal-entity-ref";
import { GroupsOfEmployeeSTO } from "../../../../models/employee-groups/groups-of-employees";
import { EmployeeDTO, EmployeeSTO } from "../../../../models/employees";
import AddEditButton from "../../../../reusable-components/AddEditButton/AddEditButton";
import AutocompleteDropdown from "../../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";

import EmailIsNotValid from "../../../../reusable-components/EmailIsNotValid/EmailIsNotValid";
import { CompanyLegalEntitiesApiService } from "../../../../services/company-legal-entities/company-legal-entities-api.service";

import { EmployeeGroupsApiService } from "../../../../services/employee-groups/employee-groups-api.service";
import { EmployeesApiService } from "../../../../services/employees/employees-api.service";
import { getNormaleDateOrMonth, isValidDate } from "../../../../utils/time-utils";
import { toastifyResponse } from "../../../../utils/toastify-response/toastify-response";

import {
  EMAIL_PATTERN,
  isValidString,
  listOfDisallowedCharsInEmailAddress,
} from "../../../../utils/validation/isValidString";

export const AddEditEmployees = ({
  effect = "add",
  asLink = false,
  linkText,
  item,
  defaultLegalEntity = null,
  handleNotifyParent,
  buttonVariant,
  handleReturnItemToEmployee,
  buttonText,
  customButtonStyle,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "pages.company_account_profile.tables.employees";
  const translationFields = `${tRP}.fields`;

  const { currentCompanyAccount } = useAppSelector((state) => state.currentUserReducer);

  const DEFAULT_ITEM: Partial<EmployeeDTO> = useMemo(() => {
    return {
      legalEntity: defaultLegalEntity ?? item?.legalEntity ?? null,
      firstName: item?.firstName ?? "",
      lastName: item?.lastName ?? "",
      employeeCode: item?.employeeCode ?? "",
      birthDate: item?.birthDate ? item?.birthDate : new Date(),
      taxId: item?.taxId ?? "",
      department: item?.department ?? "",
      groups: item?.groups ?? [],
      tnAGroup: item?.tnAGroup ?? null,
    };
  }, [defaultLegalEntity, item]);

  const INITIAL_STATE: State = useMemo(() => {
    return {
      isDialogOpen: false,
      loading: false,
      error: "",
      employeeItem: item ?? (DEFAULT_ITEM as EmployeeDTO),
      isCCDOpen: false,
      areFieldsValidated: effect === "edit",
      isValidEmail: true,
    };
  }, [effect, DEFAULT_ITEM, item]);

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  const handleChangeField = useCallback((field: keyof EmployeeDTO, value: EmployeeDTO[keyof EmployeeDTO]) => {
    setState((state) => ({
      ...state,
      employeeItem: {
        ...state.employeeItem,
        [field]: value,
      },
    }));
  }, []);

  const handleSave = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));

    const employeeId = state.employeeItem?.id ? state.employeeItem?.id : uuidv4();

    const memberOfGroups = {
      id: uuidv4(),
      employee: {
        id: employeeId,
      },
      groups: state.employeeItem.groups,
      tnAGroup: state.employeeItem.tnAGroup,
    };

    const employee = (({ groups, tnAGroup, ...o }) => o)(state.employeeItem);

    const employeeBirthDate = employee?.birthDate
      ? `${new Date(employee.birthDate).getFullYear()}-${getNormaleDateOrMonth(
          new Date(employee.birthDate).getMonth() + 1
        )}-${getNormaleDateOrMonth(new Date(employee.birthDate).getDate())}`
      : null;

    try {
      const response = await new EmployeesApiService().saveItems([
        {
          ...employee,
          firstName: employee.firstName.trim(),
          lastName: employee.lastName.trim(),
          birthDate: employeeBirthDate,
          id: employeeId,
          memberOfGroups: memberOfGroups as GroupsOfEmployeeSTO,
          email: state.employeeItem.email?.toLowerCase().trim(),
        } as EmployeeSTO,
      ]);

      if (!response.error) {
        effect === "add" && setState({ ...INITIAL_STATE });
        effect === "edit" &&
          setState((state) => ({
            ...state,
            isDialogOpen: false,
            loading: false,
            isCCDOpen: false,
          }));
        handleNotifyParent && handleNotifyParent();

        handleReturnItemToEmployee &&
          handleReturnItemToEmployee({
            ...employee,
            name: `${employee.firstName} ${employee.lastName}`,
            id: employeeId,
            groups: state.employeeItem.groups,
            tnAGroup: state.employeeItem.tnAGroup,
          } as EmployeeDTO);
      } else {
        setState(() => ({ ...state, error: response.error }));
      }

      toastifyResponse(response);
    } catch (error) {
      setState((state) => ({
        ...state,
        loading: false,
        error: error as string,
      }));
    }
  }, [INITIAL_STATE, effect, handleNotifyParent, state.employeeItem]);

  const handleDiscardChanges = useCallback(() => {
    setState({ ...INITIAL_STATE });
  }, [INITIAL_STATE]);

  const resetAndCloseDialog = (): void => {
    if (!isEqual(state.employeeItem, INITIAL_STATE.employeeItem)) {
      setState((state) => ({
        ...state,
        isCCDOpen: true,
      }));
    } else {
      handleDiscardChanges();
    }
  };

  const checkEmailForValidation = useCallback((value: string) => {
    if (!value) {
      setState((state) => ({
        ...state,
        isValidEmail: true,
      }));
    } else {
      setState((state) => ({
        ...state,
        isValidEmail: isValidString(EMAIL_PATTERN, value),
      }));
    }
  }, []);

  useEffect(() => {
    effect === "add" &&
      setState((state) => ({
        ...state,
        employeeItem: {
          ...state.employeeItem,
          legalEntity: defaultLegalEntity ? (defaultLegalEntity as CompanyLegalEntityRef) : null,
        },
      }));
  }, [effect, defaultLegalEntity, currentCompanyAccount]);

  useEffect(() => {
    setState((state) => ({
      ...state,
      employeeItem: {
        ...state.employeeItem,
      },
    }));
  }, [state.employeeItem.legalEntity, currentCompanyAccount?.defaultCurrency]);

  const areFieldsValidated = useCallback((): boolean => {
    return (
      !!state.employeeItem.legalEntity &&
      !!state.employeeItem.firstName &&
      !!state.employeeItem.lastName &&
      !!state.employeeItem.employeeCode &&
      !!isValidDate(state.employeeItem.birthDate) &&
      (!!state.employeeItem.groups?.length || !!state.employeeItem.tnAGroup)
    );
  }, [state.employeeItem]);

  useEffect(() => {
    setState((state) => ({
      ...state,
      areFieldsValidated: areFieldsValidated(),
    }));
  }, [state.employeeItem, areFieldsValidated]);

  useEffect(() => {
    !!item &&
      setState((state) => ({
        ...state,
        employeeItem: item,
      }));

    return () => {
      if (effect === "add") setState({ ...INITIAL_STATE });
    };
  }, [item, effect, INITIAL_STATE]);

  return (
    <>
      <AddEditButton
        title={t(`buttons.${effect}`)}
        effect={effect}
        asLink={asLink}
        linkText={linkText}
        onClick={() => {
          setState((state) => ({ ...state, isDialogOpen: true }));
        }}
        {...(buttonVariant === "text" && {
          buttonText: buttonText ? buttonText : effect === "edit" ? `${t("buttons.edit")}` : "Add new employee",
        })}
        customStyle={customButtonStyle}
      />
      <Dialog
        open={state.isDialogOpen}
        onClose={resetAndCloseDialog}
        fullWidth
        maxWidth="sm"
      >
        <LoadingOverlay
          loading={state.loading}
          error={state.error}
        >
          <DialogTitle
            title={effect === "add" ? t(`${tRP}.title_for_add_component`) : t(`${tRP}.title_for_edit_component`)}
            handleClose={resetAndCloseDialog}
          />
          <DialogContent>
            <FieldsContainer>
              <Box>
                <AutocompleteDropdown
                  labelKey={t("common_words.legal_entity")}
                  field={"legalEntity"}
                  serviceApi={new CompanyLegalEntitiesApiService()}
                  handleChange={handleChangeField}
                  selectedValue={state.employeeItem.legalEntity ?? null}
                  disabled={effect === "edit" || !!defaultLegalEntity}
                />
                <TextField
                  required
                  label={t(`${translationFields}.first_name`)}
                  value={state.employeeItem.firstName}
                  onChange={({ target: { value } }) => handleChangeField("firstName", value)}
                />
              </Box>

              <Box>
                <TextField
                  required
                  label={t(`${translationFields}.last_name`)}
                  value={state.employeeItem.lastName}
                  onChange={({ target: { value } }) => handleChangeField("lastName", value)}
                />
                <DesktopDatePicker
                  inputFormat="dd.MM.yyyy"
                  mask="__.__.____"
                  label={t(`${translationFields}.birth_date`)}
                  value={state.employeeItem?.birthDate}
                  onChange={(newValue) => {
                    newValue && handleChangeField("birthDate", newValue);
                  }}
                  renderInput={(params) => <TextField {...params} />}
                />
              </Box>

              <Box>
                <TextField
                  required
                  label={t(`${translationFields}.employee_code`)}
                  value={state.employeeItem.employeeCode}
                  onChange={({ target: { value } }) => handleChangeField("employeeCode", value)}
                />
                <TextField
                  label={t(`${translationFields}.email`)}
                  value={state.employeeItem.email}
                  onChange={({ target: { value } }) => {
                    setState((state) => ({
                      ...state,
                      isValidEmail: true,
                    }));

                    handleChangeField("email", value.trim());
                  }}
                  onKeyDown={(e) => {
                    if (listOfDisallowedCharsInEmailAddress.includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onBlur={(e) => checkEmailForValidation(e.target.value)}
                  InputProps={{
                    endAdornment: <>{!state.isValidEmail && <EmailIsNotValid />}</>,
                  }}
                  error={!state.isValidEmail}
                />
              </Box>

              <Box>
                <TextField
                  label={t(`${translationFields}.tax_id`)}
                  value={state.employeeItem.taxId}
                  onChange={({ target: { value } }) => handleChangeField("taxId", value)}
                />
                <TextField
                  label={t(`${translationFields}.department`)}
                  value={state.employeeItem.department}
                  onChange={({ target: { value } }) => handleChangeField("department", value)}
                />
              </Box>

              <Box>
                <AutocompleteDropdown
                  labelKey={"common_words.employee_groups"}
                  field={"groups"}
                  serviceApi={new EmployeeGroupsApiService()}
                  handleChange={handleChangeField}
                  selectedValue={state.employeeItem.groups}
                  propsItem={state.employeeItem}
                  isMultiSelect
                  required
                />
                <AutocompleteDropdown
                  labelKey={t(`${tRP}.link_employee_to_employee_groups.tna_group`)}
                  field={"tnAGroup"}
                  serviceApi={new EmployeeGroupsApiService()}
                  handleChange={handleChangeField}
                  selectedValue={state.employeeItem.tnAGroup}
                  required
                />
              </Box>
            </FieldsContainer>
          </DialogContent>
          <DialogActions
            handleSave={handleSave}
            disabledSaveButton={!areFieldsValidated() || (!!state.employeeItem.email && !state.isValidEmail)}
            handleCancel={resetAndCloseDialog}
          />
        </LoadingOverlay>
      </Dialog>

      <ConfirmationChangesDialog
        open={state.isCCDOpen}
        handleCancel={() => setState({ ...state, isCCDOpen: false })}
        handleDiscard={handleDiscardChanges}
      />
    </>
  );
};
